
import React, { useState, useEffect } from 'react';
import { useLeadsData } from '@/hooks/useLeadsData';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Phone, 
  Mail, 
  Calendar, 
  User, 
  Building2, 
  Euro,
  TrendingUp,
  Plus,
  RefreshCw,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import LeadCard from './LeadCard';
import LeadDetailModal from './LeadDetailModal';

const LEAD_STATUSES = [
  { value: 'nieuw', label: 'Nieuw', color: 'bg-blue-500' },
  { value: 'contact_opgenomen', label: 'Contact Opgenomen', color: 'bg-yellow-500' },
  { value: 'interesse', label: 'Interesse', color: 'bg-orange-500' },
  { value: 'offerte_verstuurd', label: 'Offerte Verstuurd', color: 'bg-purple-500' },
  { value: 'gesloten', label: 'Gesloten', color: 'bg-green-500' },
  { value: 'verloren', label: 'Verloren', color: 'bg-red-500' }
];

const LeadsManagement = () => {
  const { leads, loading, refetch, updateLeadStatus, addLeadNote } = useLeadsData();
  const [selectedLead, setSelectedLead] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredLeads = leads.filter(lead => 
    lead.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.business_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getLeadsByStatus = (status: string) => {
    return filteredLeads.filter(lead => lead.lead_status === status);
  };

  const handleDragStart = (e: React.DragEvent, leadId: string) => {
    e.dataTransfer.setData('text/plain', leadId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('text/plain');
    await updateLeadStatus(leadId, newStatus as any);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-2 text-gray-600">Laden...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Lead Management</h2>
          <p className="text-gray-600">Beheer en volg submissions als leads op</p>
        </div>
        <div className="flex items-center gap-4">
          <Input
            placeholder="Zoek leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <Button variant="outline" onClick={refetch}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Vernieuwen
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totaal Leads</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{leads.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Actieve Leads</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {leads.filter(l => !['gesloten', 'verloren'].includes(l.lead_status)).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gesloten</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {leads.filter(l => l.lead_status === 'gesloten').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <Euro className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {leads.length > 0 
                ? Math.round((leads.filter(l => l.lead_status === 'gesloten').length / leads.length) * 100)
                : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 overflow-x-auto">
        {LEAD_STATUSES.map(status => {
          const statusLeads = getLeadsByStatus(status.value);
          return (
            <div
              key={status.value}
              className="bg-gray-50 rounded-lg p-4 min-h-[600px]"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status.value)}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${status.color}`}></div>
                  <h3 className="font-semibold text-gray-900">{status.label}</h3>
                </div>
                <Badge variant="outline">{statusLeads.length}</Badge>
              </div>
              
              <div className="space-y-3">
                {statusLeads.map(lead => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    onDragStart={(e) => handleDragStart(e, lead.id)}
                    onClick={() => setSelectedLead(lead.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          leadId={selectedLead}
          onClose={() => setSelectedLead(null)}
        />
      )}
    </div>
  );
};

export default LeadsManagement;
